// "Your HR requests" at the foot of the pane, with the same search, delete and clear all as the
// chat list above it. Deleting only takes a request off the employee's list: HR keeps it, and
// the confirmation says so.

import { useEffect, useRef, useState } from "react";
import { CATEGORIES, STATUS_LABELS, type EmployeeRequest } from "@ask-hr/core/escalations";
import { api } from "../api.js";
import { linkTo, navigate } from "../router.js";
import { SearchIcon, TrashIcon, highlight, relative } from "./listing.js";
import { STATUS_PILL, hasUnseenComment } from "./MyRequest.js";

export function RequestsPane({
  requests,
  currentId,
  onChanged,
}: {
  requests: EmployeeRequest[];
  currentId: string | null;
  onChanged: () => void;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const toggleSearch = () => {
    if (searchOpen) setSearch("");
    setSearchOpen(!searchOpen);
  };

  async function remove(id: string) {
    try {
      await api("DELETE", `/api/escalations/${id}`);
    } catch {}
    setConfirmDelete(null);
    if (id === currentId) navigate("/");
    onChanged();
  }

  async function clearAll() {
    try {
      await api("DELETE", "/api/escalations");
    } catch {}
    setConfirmAll(false);
    if (currentId) navigate("/");
    onChanged();
  }

  const term = search.trim().toLowerCase();
  const title = (r: EmployeeRequest) => CATEGORIES[r.category];
  const shown = requests.filter((r) => !term || `${title(r)} ${r.summary}`.toLowerCase().includes(term));
  const snippet = (r: EmployeeRequest) => {
    // While searching, show the note around the match so it's clear why it matched.
    const i = term ? r.summary.toLowerCase().indexOf(term) : -1;
    return i > 20 ? `…${r.summary.slice(i - 12)}` : r.summary;
  };

  if (!requests.length) return null;
  return (
    <div className="my-requests">
      <div className="my-requests-head">
        <h3>Your HR requests</h3>
        <button
          type="button"
          className="iconbtn small"
          aria-label="Search HR requests"
          title="Search HR requests"
          aria-pressed={searchOpen}
          onClick={toggleSearch}
        >
          <SearchIcon />
        </button>
        <button
          type="button"
          className="iconbtn small"
          aria-label="Clear all HR requests"
          title="Clear all HR requests"
          aria-expanded={confirmAll}
          onClick={() => setConfirmAll(!confirmAll)}
        >
          <TrashIcon />
        </button>
      </div>
      {searchOpen ? (
        <label className="chat-search">
          <SearchIcon />
          <span className="sr-only">Search HR requests</span>
          <input
            ref={searchRef}
            type="search"
            placeholder="Search requests"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") toggleSearch();
            }}
          />
        </label>
      ) : null}
      {confirmAll ? (
        <div className="confirm-all" role="alert">
          <span>
            Remove all <b>{requests.length}</b> requests from your list? HR keeps them; this only clears them from Ask HR for you.
          </span>
          <div className="actions start">
            <button type="button" className="danger" onClick={() => void clearAll()}>
              Remove all
            </button>
            <button type="button" onClick={() => setConfirmAll(false)}>
              Keep
            </button>
          </div>
        </div>
      ) : null}
      {shown.length === 0 ? (
        <p className="sub">No requests match.</p>
      ) : (
        <ul>
          {shown.map((r) => (
            <li key={r.id} className={r.id === currentId ? "current" : undefined}>
              {confirmDelete === r.id ? (
                <div className="confirm">
                  <span>Remove from your list? HR keeps it.</span>
                  <button type="button" className="danger" onClick={() => void remove(r.id)}>
                    Remove
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(null)}>
                    Keep
                  </button>
                </div>
              ) : (
                <>
                  <a
                    href={`/requests/${r.id}`}
                    onClick={linkTo(`/requests/${r.id}`)}
                    aria-current={r.id === currentId ? "page" : undefined}
                    title={`Sent ${relative(r.createdAt)}${r.summary ? `: ${r.summary}` : ""}`}
                  >
                    <span className="row">
                      <span className="title">{highlight(title(r), term)}</span>
                      {hasUnseenComment(r) ? (
                        <span className="new-comment" title="New comment from HR">
                          <span className="sr-only"> (new comment from HR)</span>
                        </span>
                      ) : null}
                      <span className={STATUS_PILL[r.status]}>{STATUS_LABELS[r.status]}</span>
                    </span>
                    {/* Only while searching, and only when the match is in their note: shows why it matched. */}
                    {term && !title(r).toLowerCase().includes(term) && r.summary.toLowerCase().includes(term) ? (
                      <span className="snippet">{highlight(snippet(r), term)}</span>
                    ) : null}
                  </a>
                  <button type="button" className="icon" aria-label={`Remove "${title(r)}" from your list`} onClick={() => setConfirmDelete(r.id)}>
                    ×
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
